<template>
  <div class="filters-container w-100" :class="loading ? 'disabled' : ''">
    <div
      class="d-flex flex-row justify-content-between align-items-center bg-light-gray min-width-fit-content"
    >
      <div class="d-flex flex-wrap align-items-center">
        <b-form-input
          class="auth-input search-input border-color-light-gray"
          v-model="searchPhrase"
          placeholder="Search by patient or document #..."
          :readonly="loading"
        />
        <span class="ml-4 mr-3" style="font-size: 14px;">Active filters:</span>
        <div
          class="filter-value bg-white border pr-2 pt-1 pb-1 m-1"
          v-if="searchPhrase.length >= 3"
        >
          <div class="d-flex justify-content-between align-items-center">
            <span class="mr-3"
              >Search Bar:
              <span class="color-filter">{{ searchPhrase }}</span></span
            >
            <i
              class="fa fa-times text-danger hover-pointer hover-text-almost-black"
              @click="searchPhrase = ''"
            ></i>
          </div>
        </div>
        <div
          class="filter-value bg-white border pr-2 pt-1 pb-1 m-1"
          v-for="(pat, patientIndex) in patient"
          :key="pat.value"
        >
          <div class="d-flex justify-content-between align-items-center">
            <span class="mr-3"
              >Patients: <span class="color-filter">{{ pat.label }}</span></span
            >
            <i
              class="fa fa-times text-danger hover-pointer hover-text-almost-black"
              @click="removeIndexFromFilter('patientId', patientIndex)"
            ></i>
          </div>
        </div>

        <div
          class="filter-value bg-white border pr-2 pt-1 pb-1 m-1"
          v-for="(payment, paymentIndex) in payments"
          :key="payment.value"
        >
          <div class="d-flex justify-content-between align-items-center">
            <span class="mr-3"
              >Payment: <span class="color-filter">{{ payment.label }}</span></span
            >
            <i
              class="fa fa-times text-danger hover-pointer hover-text-almost-black"
              @click="removeIndexFromFilter('payments', paymentIndex)"
            ></i>
          </div>
        </div>

        <div
          class="filter-value bg-white border pr-2 pt-1 pb-1 m-1"
          v-for="(cc, ccOnFileIndex) in ccOnFile"
          :key="cc.value"
        >
          <div class="d-flex justify-content-between align-items-center">
            <span class="mr-3"
              >CC on file: <span class="color-filter">{{ cc.label }}</span></span
            >
            <i
              class="fa fa-times text-danger hover-pointer hover-text-almost-black"
              @click="removeIndexFromFilter('ccOnFile', ccOnFileIndex)"
            ></i>
          </div>
        </div>

        <div
          class="filter-value bg-white border pr-2 pt-1 pb-1 m-1"
          v-for="(user, userIndex) in createdBy"
          :key="user.value"
        >
          <div class="d-flex justify-content-between align-items-center">
            <span class="mr-3"
              >Created by: <span class="color-filter">{{ user.label }}</span></span
            >
            <i
              class="fa fa-times text-danger hover-pointer hover-text-almost-black"
              @click="removeIndexFromFilter('createdBy', userIndex)"
            ></i>
          </div>
        </div>

        <div
          v-if="dateFrom || dateTo"
          class="filter-value bg-white border pr-2 pt-1 pb-1 m-1"
        >
          <div class="d-flex justify-content-between align-items-center">
            <span class="mr-3"
              >Date:
              <span class="color-filter">{{
                dateFrom || dateTo
                  ? `(${dateFrom ? "From: " + formatDateOnly(dateFrom) : ""}${
                      dateFrom && dateTo ? " - " : ""
                    }${dateTo ? "To: " + formatDateOnly(dateTo) : ""})`
                  : ""
              }}</span></span
            >
            <i
              class="fa fa-times text-danger hover-pointer hover-text-almost-black"
              @click="removeDateFilter()"
            ></i>
          </div>
        </div>
        <span
          v-if="hasFilters"
          class="clear-all-filters text-danger ml-2"
          style="font-size: 14px;"
          @click="clearAllFilters"
          >Clear all filters</span
        >
      </div>
      <div class="d-flex justify-content-end">
        <div class="d-flex align-self-stretch align-items-center">
          <div class="align-self-stretch" style="padding: 9px 0;">
            <div class="vertical-separator border-med-light-gray"></div>
          </div>
          <b-dropdown
            id="dropdown-form"
            text="Filters"
            ref="filterDropdown"
            class="container-main bg-light-gray"
            toggle-class="d-flex flex-row justify-content-between align-items-lg-center bg-transparent border-0 color-custom-dark-grey dropdown-menu-label"
            menu-class="container-filter-main-menu"
            toggle-tag="div"
            boundary="window"
            right
            @hide="onFilterHide"
          >
            <template #button-content>
              <div class="d-flex align-items-center mr-2">
                <i
                  class="fas fa-filter text-primary mr-2"
                  style="font-size: 15px;"
                  aria-hidden="true"
                ></i>
                <span style="font-size: 14px;">Filters</span>
              </div>
            </template>
            <b-dropdown-form class="px-2">
              <b-form-group label="Filter by:">
                <b-form-select
                  v-model="selectedFilter"
                  :options="filterOptions"
                />
              </b-form-group>

              <multiple-select-filter
                v-if="selectedFilter === 'patient'"
                :label="'Patients'"
                :options="allPatients"
                v-model="patient"
                :width="'250px'"
                :showSearch="true"
                :async="true"
                :searching="searchingPatients"
                @on-search="fetchPatients"
                @on-text-selection="updateTextSelection"
                :key="'patient'"
                ref="patientFilter"
              >
                <template v-slot:item-option="{ itemOption, selected }">
                  <eoc-filter-option-template
                    :eoc="itemOption.data"
                    :selected="selected"
                  />
                </template>
              </multiple-select-filter>
              <date-range-filter
                v-if="selectedFilter === 'date'"
                v-model="filterDateRange"
                :filterWidth="'250px'"
                label="Date"
                :key="'date'"
                :maxDate="new Date()"
              />

              <multiple-select-filter
                v-if="selectedFilter === 'createdBy'"
                :label="'Created by'"
                :options="createdByOptions"
                v-model="createdBy"
                :width="'250px'"
                :showSearch="true"
                @on-text-selection="updateTextSelection"
                :key="'createdBy'"
                ref="createdByFilter"
              />
              
              <multiple-select-filter
                v-if="selectedFilter === 'payment'"
                :label="'Payment'"
                :options="paymentOptions"
                v-model="payments"
                :width="'250px'"
                :key="'payment'"
                ref="paymentFilter"
              />

              <multiple-select-filter
                v-if="selectedFilter === 'ccOnFile'"
                :label="'CC on file'"
                :options="ccOnFileOptions"
                v-model="ccOnFile"
                :width="'250px'"
                :key="'ccOnFile'"
                ref="ccOnFileFilter"
              />

            </b-dropdown-form>
          </b-dropdown>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import userServer from "../../../server/user";
import EocFilterOptionTemplate from "../../../views/EocFilterOptionTemplate.vue";
import eocServer from "../../../server/eoc";

export default {
  name: "BillingDocumentIndexFilters",
components: { EocFilterOptionTemplate } ,
  props: ["value", "hasFilters", "loading", "showing"],
  data() {
    return {
      selectedFilter: "patient",
      filterOptions: [
        {
          value: "patient",
          text: "Patient"
        },
        {
          value: "date",
          text: "Date"
        },
        {
          value: "createdBy",
          text: "Created By"
        },
        {
          value: "ccOnFile",
          text: "CC on File"
        },
        {
          value: "payment",
          text: "Payment"
        },
      ],
      users: [],
      allPatients: [],
      allLocations: [],
      createdByOptions: [],
      searchingPatients: false,
      refetchData: false,
      filterTextSelection: null,
      paymentOptions: [
        {
          value: "paid",
          label: "Paid"
        },
        {
          value: "unpaid",
          label: "Unpaid"
        },
      ],
      ccOnFileOptions: [
        {
          value: "yes",
          label: "Yes"
        },
        {
          value: "no",
          label: "No"
        },
      ],
    };
  },
  computed: {
    patients() {
      return this.allPatients.map(patient => ({
        value: patient.id,
        label: patient.name
      }));
    },
    patient: {
      get() {
        return this.value.patientId;
      },
      set(newValue) {
        this.$emit("input", { ...this.value, patientId: newValue });
      }
    },
    filterDateRange: {
      get() {
        return {
          dateFrom: this.value.dateFrom,
          dateTo: this.value.dateTo
        };
      },
      set(newValue) {
        this.$emit("input", {
          ...this.value,
          dateFrom: newValue.dateFrom,
          dateTo: newValue.dateTo
        });
      }
    },
    dateFrom: {
      get() {
        return this.value.dateFrom;
      },
      set(newValue) {
        this.$emit("input", { ...this.value, dateFrom: newValue });
      }
    },
    dateTo: {
      get() {
        return this.value.dateTo;
      },
      set(newValue) {
        this.$emit("input", { ...this.value, dateTo: newValue });
      }
    },
    searchPhrase: {
      get() {
        return this.value.searchPhrase;
      },
      set(newValue) {
        this.$emit("input", { ...this.value, searchPhrase: newValue });
      }
    },
    payments: {
      get() {
        return this.value.payments;
      },
      set(newValue) {
        this.$emit("input", { ...this.value, payments: newValue });
      }
    },
    ccOnFile: {
      get() {
        return this.value.ccOnFile;
      },
      set(newValue) {
        this.$emit("input", { ...this.value, ccOnFile: newValue });
      }
    },
    createdBy: {
      get() {
        return this.value.createdBy;
      },
      set(newValue) {
        this.$emit("input", { ...this.value, createdBy: newValue });
      }
    },
  },
  mounted() {
    this.refetchData = true;
  },
  methods: {
    fetchInitialData() {
      this.fetchPatients();
      this.fetchUsers();
    },
    async fetchPatients(search) {
      const self = this;
      if (this.lastTimeoutClientOptions) {
        clearTimeout(this.lastTimeoutClientOptions);
        this.lastTimeoutClientOptions = null;
      }
      this.lastTimeoutClientOptions = setTimeout(async () => {
        if (!search || search.length === 0 || search.length >= 3) {
          this.searchingClients = true;
          const allPatients = await eocServer.queryList(
            {
              searchPhrase: search,
              eocStatus:
                !search || search.length === 0 ? ["Active", "Pending"] : [],
              page: 1,
              pageSize: 50
            },
            {
              "Hide-Loading": true
            }
          );
          self.allPatients = [];
          self.allPatients = allPatients.episodesOfCare.map(eoc => ({
            value: eoc.patientId,
            label: this.formatEocObj(eoc),
            data: eoc
          }));
        }
      }, 1000);
    },
    clearAllFilters() {
      this.$emit("input", {
        ...this.value,
        patientId: [],
        dateFrom: null,
        dateTo: null,
        createdBy: [],
        payments: [],
        ccOnFile: [],
      });
    },
    removeIndexFromFilter(filterName, index) {
      const filter = [...this.value[filterName]];
      filter.splice(index, 1);
      this.$emit("input", { ...this.value, [filterName]: filter });
    },
    removeDateFilter() {
      this.$emit("input", {
        ...this.value,
        dateFrom: null,
        dateTo: null
      });
    },
    updatePopper() {
      this.$refs.filterDropdown.updatePopper();
    },
    onFilterHide(event) {
      let ref = null;
      if (this.selectedFilter === "patient") {
        ref = this.$refs.patientFilter;
      }
      if (this.filterTextSelection) {
        if (this.filterTextSelection) event.preventDefault();
        if (ref != null) ref.highlightText();
        this.filterTextSelection = null;
      }
    },
    updateTextSelection(textSelection) {
      this.filterTextSelection = textSelection;
    },
    async fetchUsers() {
      const users = await userServer.getMinimalUsers({
        overrideAccessToLocations: true
      });
      this.createdByOptions = users.map(user => ({
        value: user.id,
        label: this.formatUser(user)
      }));
    },
  },
  watch: {
    selectedFilter: function() {
      this.$refs.filterDropdown.updatePopper();
    },
    showing: function(newValue) {
      if (newValue && this.refetchData) {
        this.fetchInitialData();
      }
    }
  }
};
</script>

<style scoped>
.filters-container {
  overflow-x: auto;
}

.filters-container >>> .container-filter-main-menu {
  min-width: 250px;
  left: -17px;
}

.filter-value {
  min-width: 100px;
  height: 29px;
  color: #a6a6a6;
  font-size: 14px;
  padding-left: 10px;
}

.search-input {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  width: 320px;
}

.color-filter {
  color: #909db8;
}

.clear-all-filters:hover {
  cursor: pointer;
}

.vertical-separator {
  width: 1px;
  border-left: 1px solid;
  height: 100%;
}
</style>
